import { LoginError } from 'inter-extension-control/module/src/errors/LoginError';
import { GenericError } from 'inter-extension-control/module/src/errors/GenericError';
import { getExtensionVersion } from '../chrome/api';
import { fullOnboardingWithDelegation } from '../onboarding/authentication';
import { captureException, Severity } from '../helper/sentry';
import { APIError } from '../lib/errors/api/APIError';
import { APITimeoutError } from '../lib/errors/api/APITimeoutError';
import { logout } from '../onboarding/logout';
import { isLoggedInWithDelegation } from './delegation';
import { JWT_TOKEN_TAG } from '../lib/vendor/sentry';

/**
 * Onboards the extension with delegation.
 *
 * @param token - JWT token to call the delegation authentication endpoint
 *
 * @throws LoginError
 * This exception is thrown if the login fails (e.g: Extension is already logged-in, API error, timeout...)
 */
export const loginWithDelegation = async (token: string) => {
  // Check if it's already logged-in, and throw an error if so.
  if (isLoggedInWithDelegation()) {
    const error = new LoginError(
      'ALREADY_LOGGED_IN',
      getExtensionVersion(),
      'Login failed because the extension is already logged-in.'
    );
    console.warn(error);
    captureException(error, {
      level: Severity.Warning,
      extra: {
        [JWT_TOKEN_TAG]: token,
      },
    });
    throw error;
  }

  // Attempt onboarding, and throw error if any.
  try {
    return await fullOnboardingWithDelegation(token);
  } catch (error) {
    console.error(error);
    captureException(error as Error, {
      extra: {
        [JWT_TOKEN_TAG]: token,
      },
    });

    if (error instanceof APITimeoutError) {
      throw new LoginError('TIMEOUT', getExtensionVersion(), error.message);
    } else if (error instanceof APIError) {
      throw new LoginError('API_ERROR', getExtensionVersion(), error.message);
    }
    throw new LoginError(
      'UNKNOWN',
      getExtensionVersion(),
      (error as Error).message
    );
  }
};

/**
 * Logs out the extension if it's logged-in with delegation.
 *
 * @throws GenericError of type 'NOT_LOGGED_IN'
 * This exception is thrown if the extension it's not logged-in with delegation.
 */
export const logoutDelegation = () => {
  if (isLoggedInWithDelegation()) {
    return logout();
  }

  const error = new GenericError(
    'NOT_LOGGED_IN',
    getExtensionVersion(),
    'Logout failed because the extension is not logged-in.'
  );
  console.warn(error);
  captureException(error, { level: Severity.Warning });
  return Promise.reject(error);
};
